import * as React from 'react'
import { theme } from '../theme'
import { InpaintTools } from '../inpaint_and_erase/InpaintTools'
import { EraseTools } from '../inpaint_and_erase/EraseTools'
import { DisclaimerTextInput } from '../disclaimer_texts/DisclaimerTextInput'

type TDrawingTool = 'brush' | 'eraser' | null

interface Props {
	currentTool: TDrawingTool
	isEditing: boolean
	onToolSelect: (tool: TDrawingTool) => void
	hasMask: boolean
	currentPrompt: string
	onUndo: () => void
	onAccept: () => void
	brushSize: number
	onBrushSizeChange: (size: number) => void
	disclaimerText: string
	onDisclaimerTextChange: (text: string) => void
	onDisclaimerAccept: () => void
	isProcessing?: boolean
	selectedAction?: string
	areAllAspectRatiosValid?: boolean
	onSwitchToAdjustTool?: () => void
	textAlignment?: 'left' | 'center'
	onTextAlignmentChange?: (alignment: 'left' | 'center') => void
	onIncreaseFontSize?: () => void
	onDecreaseFontSize?: () => void
}

export const ToolSelector = ({
	currentTool,
	isEditing,
	onToolSelect,
	hasMask,
	currentPrompt,
	onUndo,
	onAccept,
	brushSize,
	onBrushSizeChange,
	disclaimerText,
	onDisclaimerTextChange,
	onDisclaimerAccept,
	isProcessing,
	selectedAction,
	areAllAspectRatiosValid = true,
	onSwitchToAdjustTool,
	textAlignment = 'left',
	onTextAlignmentChange,
	onIncreaseFontSize,
	onDecreaseFontSize,
}: Props) => {
	// Automatically select the appropriate tool when the action changes
	React.useEffect(() => {
		switch (selectedAction) {
			case 'add':
				onToolSelect('brush')
				break
			case 'erase':
				onToolSelect('eraser')
				break
			case 'adjust':
			case 'disclaimer':
			case 'create':
			default:
				// Set tool to null for all other actions
				onToolSelect(null)
				break
		}
	}, [selectedAction, onToolSelect])

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				gap: theme.spacing.lg,
				padding: theme.spacing.md,
				width: '100%',
				backgroundColor: theme.colors.surface,
				borderRadius: theme.borderRadius.sm,
				border: `1px solid ${theme.colors.border}`,
			}}
		>
			{/* Inpainting Tools Section */}
			{selectedAction === 'add' && (
				<InpaintTools
					currentTool={currentTool}
					isEditing={isEditing}
					onToolSelect={onToolSelect}
					hasMask={hasMask}
					currentPrompt={currentPrompt}
					onUndo={onUndo}
					onAccept={onAccept}
					brushSize={brushSize}
					onBrushSizeChange={onBrushSizeChange}
				/>
			)}

			{/* Erase Tools Section */}
			{selectedAction === 'erase' && (
				<EraseTools
					currentTool={currentTool}
					isEditing={isEditing}
					onToolSelect={onToolSelect}
					hasMask={hasMask}
					onUndo={onUndo}
					onAccept={onAccept}
					brushSize={brushSize}
					onBrushSizeChange={onBrushSizeChange}
				/>
			)}

			{/* Disclaimer Text Section */}
			{selectedAction === 'disclaimer' && (
				<DisclaimerTextInput
					value={disclaimerText}
					onChange={onDisclaimerTextChange}
					disabled={isProcessing}
					onAccept={onDisclaimerAccept}
					isProcessing={isProcessing}
					areAllAspectRatiosValid={areAllAspectRatiosValid}
					onSwitchToAdjustTool={onSwitchToAdjustTool}
					textAlignment={textAlignment}
					onTextAlignmentChange={onTextAlignmentChange}
					onIncreaseFontSize={onIncreaseFontSize}
					onDecreaseFontSize={onDecreaseFontSize}
				/>
			)}
		</div>
	)
}
